import { getAuthCookie, canReadWriteCookies } from '../cookies';
import { getGraphQLClient } from '@simplywallst/services';
import type { GraphQLClient, RequestDocument } from 'graphql-request';

import { setContext } from '@sentry/react';
import { getPreferredLanguage } from './language';

interface SWSGQLHeaders {
  'x-sws-pro-api': string;
}

type SWSGQLErrorType = {
  message?: string;
  path?: string[];
  extensions?: {
    code: string;
    statusCode: number;
    serviceName: string;
    traceId: string;
  };
};

export const isErrors = (errors: unknown): errors is SWSGQLErrorType[] => {
  if (!Array.isArray(errors)) return false;
  if (errors.length === 0) return false;
  return errors.every((e) => {
    if (typeof e !== 'object') return false;
    if (e === null) return false;
    return 'message' in e && 'extensions' in e;
  });
};

export class SWSGQLError extends Error {
  errors: SWSGQLErrorType[] = [];
  status: number;
  code: string = 'UNKNOWN_ERROR';
  message: string = 'Unknown error';

  constructor(errors?: SWSGQLErrorType[], statusCode = 500) {
    super();
    this.status = statusCode;

    if (errors && errors.length > 0) {
      this.errors = errors;

      const [first] = errors;
      const { extensions, message } = first;

      if (extensions) {
        this.status = extensions.statusCode;
        this.code = extensions.code;
      }

      this.message = message ?? 'Unknown error';
    }
  }
}

export const isSWSGQLError = (error: unknown): error is SWSGQLError => {
  if (error === null) return false;
  if (error instanceof Error) {
    if ('errors' in error && 'code' in error && 'status' in error) {
      const { code, status, errors } = error;
      return (
        typeof code === 'string' &&
        typeof status === 'number' &&
        Array.isArray(errors)
      );
    }
  }
  return false;
};

const getHeader = (additionalHeaders: Partial<SWSGQLHeaders> = {}) => {
  let preferredLanguage: string | undefined;
  try {
    preferredLanguage = window.navigator.language;
  } catch {
    preferredLanguage = getPreferredLanguage();
  }

  return {
    'apollographql-client-name': RUNTIME_ENV === 'native' ? 'mobile' : 'web',
    ...(typeof preferredLanguage === 'string'
      ? { 'Accept-Language': preferredLanguage }
      : {}),
    ...additionalHeaders,
  };
};

export const handleError = (error: unknown) => {
  if (error instanceof Error) {
    const { response } = JSON.parse(JSON.stringify(error));
    if (typeof response === 'object' && 'errors' in response) {
      const { errors } = response;
      if (isErrors(errors)) {
        setContext('gqlQuery', { errors });
        throw new SWSGQLError(errors);
      }
    }
  }
  throw new Error('Error unknown');
};

export const getOperationName = (query: RequestDocument) => {
  if (typeof query === 'string') {
    const matches = query.match(/(?:query|mutation)\s+(\w+)\s*\(/);

    return matches ? matches[1] : null;
  }

  /**
   * Since we are using gql package from graphql-request to wrap our queries,
   * we don't need to handle DocumentNode parsing as it always returns a string with interpolated variables.
   * Should this ever change, we can add support for DocumentNode parsing.
   */
  return null;
};

interface Config {
  headers?: HeadersInit;
  baseURL?: string;
}
function makeGQLClient(
  gqlClient: (config: Config) => GraphQLClient = getGraphQLClient
) {
  return {
    request: async <T>(
      query: RequestDocument,
      variables = {},
      additionalHeaders: Partial<SWSGQLHeaders> = {}
    ) => {
      const client = gqlClient({
        headers: getHeader(additionalHeaders),
        baseURL:
          typeof SWS_INTERNAL_GQL_URL !== 'undefined'
            ? SWS_INTERNAL_GQL_URL
            : undefined,
      });

      setContext('gql', { operationName: getOperationName(query) });

      const response = await client
        .request<T>(query, variables)
        .catch(handleError);

      return response;
    },
    requestWithAuth: async <T>(
      query: RequestDocument,
      variables = {},
      additionalHeaders: Partial<SWSGQLHeaders> = {}
    ) => {
      const client = gqlClient({
        headers: getHeader(additionalHeaders),
      });
      setContext('gql', { operationName: getOperationName(query) });
      if (!canReadWriteCookies()) throw new Error('Cannot read cookies');
      const auth = await getAuthCookie();
      if (typeof auth === 'undefined') {
        throw new Error('Auth cookie is invalid');
      }
      client.setHeader('Authorization', `Bearer ${auth.accessToken}`);

      const response = await client
        .request<T>(query, variables)
        .catch(handleError);

      return response;
    },
  };
}

const defaultClient = makeGQLClient();
const { request, requestWithAuth } = defaultClient;

export { request, requestWithAuth };
